import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';

import { editJourneyNameAndDescriptionMutation } from 'api/mutations/workflows';
import { CampaignLabel } from 'modules/domains/campaign';
import { SaveJourneySettingsResponse } from 'modules/journey/components/SettingsTakeover/hooks';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { updateWorkflow } from '../ducks';
import { Workflow } from '../types';

const SUCCESS_MESSAGE = 'Success! Journey details updated.';
const FAILURE_MESSAGE = `Error updating journey details. Please try again later.`;
const FAILURE_MESSAGE_NO_NAME = `Please enter a name for your journey.`;

type useUpdateWorkflowDetailsProps = {
  workflowDetails: Pick<Workflow, 'name' | 'description' | 'workflowType'> & {
    workflowId: Workflow['id'];
  };
  onSuccess?: () => void;
  showSuccessToast?: boolean;
};

type UpdateWorkflowDetailsResponse = {
  editJourneyNameAndDescription: {
    name: string;
    description: string;
    labels?: CampaignLabel[];
  };
};

export const useUpdateWorkflowDetails = () => {
  const dispatch = useDispatch();

  const [editWorkflowDraftDetails] = useMutation<UpdateWorkflowDetailsResponse>(
    editJourneyNameAndDescriptionMutation,
  );

  return async ({
    workflowDetails,
    onSuccess,
    showSuccessToast = true,
  }: useUpdateWorkflowDetailsProps): Promise<SaveJourneySettingsResponse> => {
    if (!workflowDetails.name) {
      dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE_NO_NAME, 'editWorkflowDraftDetails'));
      return { hasErrors: true };
    }
    try {
      const { data } = await editWorkflowDraftDetails({
        variables: workflowDetails,
      });

      if (data?.editJourneyNameAndDescription?.name) {
        const { name, description, labels } = data?.editJourneyNameAndDescription;
        dispatch(updateWorkflow({ name, description, labels }));
        if (onSuccess) onSuccess();
        if (showSuccessToast) dispatch(addPositiveToast(SUCCESS_MESSAGE));
      } else {
        dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'editWorkflowDraftDetails'));
      }
      return { hasErrors: !data };
    } catch {
      dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'editWorkflowDraftDetails'));
      return { hasErrors: true };
    }
  };
};
